import { useState, useEffect, useRef, MutableRefObject } from 'react'
import { Tooltip, SvgEntity, ConfirmDialog, ToggleButton } from '@ui-kit'
import {
  DataTypeStore,
  getAllLayoutApi,
  getEntityDataByEntityIdLayoutIdApiForFilter,
  getLayoutByName,
  LayoutResponseType,
  TableFilterObjectBody,
} from '@app-store'
import { useAppSelector } from '../../utils/agent.store'
import {
  ACTIONS,
  LAYOUT_TYPE_NAMESPACE,
  MODE_CONSTANTS,
  NAMESPACES,
  PERMISSION_MODULES,
  USER_PROFILES,
} from '../../utils/constants'
import { RECORD_STATUS } from '../../constants/form.constants'
import { hasPermission, isReadOnlyUser } from '../../utils/permission'
import { ListingTable } from './ListingTable'
import { ListingTableProps } from '../../types/listingTable.types'
import { editEntityData } from '../../apis/entityData.api'
import { useParams } from 'react-router-dom'
import { permissionObject } from '../../utils/helperFunctions'

const EntityDataListingTable = ({
  entityId: propEntityId,
  entityName,
  handleRedirectId,
  setApiFilter,
  apiFilter,
}: {
  entityId: string
  entityName: string
  handleRedirectId?: (id: string, name: string) => void
  apiFilter?: MutableRefObject<TableFilterObjectBody | undefined>
  setApiFilter?: (newValue: TableFilterObjectBody) => void
}) => {
  const searchQuery = useAppSelector((state) => state?.entity?.searchQuery)
  const entities = useAppSelector((state) => state?.entity?.entities ?? [])
  const selectedClientTenantId = useAppSelector(
    (state) => state.authUser.selectedClientTenant?.id
  )
  const entitiesByName = useAppSelector(
    (state) => state?.entity?.entitiesByName
  )
  const globalPermissions = useAppSelector(
    (state) => state.clientPermissions.globalPermissions
  )
  const userData = useAppSelector((state) => state.authUser.user)
  const [tableDataKey, setTableDataKey] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [isToggleModalOpen, setIsToggleModalOpen] = useState<boolean>(false)
  const [layout, setLayout] = useState<LayoutResponseType | undefined>()
  const [currEntityName, setCurrEntityName] = useState<string>()
  const selectedRowForToggle = useRef<DataTypeStore | null>(null)
  const allLayouts = useRef<LayoutResponseType[]>([])
  const handleRedirectIdRef = useRef(handleRedirectId)
  handleRedirectIdRef.current = handleRedirectId
  const { entityId: urlEntityId } = useParams()
  const entityId = +(urlEntityId ?? propEntityId)

  const fetchUnassignedDataCallback: ListingTableProps['dataFetchCallback'] =
    async (payload) => {
      const newPayload = { ...payload, searchQuery: searchQuery }
      const response = await getEntityDataByEntityIdLayoutIdApiForFilter({
        payload: {
          ...(searchQuery ? newPayload : payload),
          ...(layout?.function_parameters
            ? { function_parameters: layout?.function_parameters }
            : {}),
        },
        entityName: entityName,
      })
      return response.data
    }
  const handleToggle = async () => {
    try {
      setIsLoading(true)
      if (selectedRowForToggle?.current?.entity_type) {
        const entityName = selectedRowForToggle?.current?.entity_type as string
        const entityObj = entitiesByName?.[entityName]
        const selectedLayout = allLayouts.current.find(
          (layout) =>
            layout.default_value &&
            layout?.type === LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING
        )
        const layout = await getLayoutByName({
          entityId: selectedLayout?.entity_id.toString(),
          layoutName: selectedLayout?.name,
        })
        await editEntityData(
          entityObj?.name,
          parseInt(entityObj?.id.toString()),
          layout?.data?.id ?? 0,
          {
            id: selectedRowForToggle?.current?.id,
            record_status:
              selectedRowForToggle?.current?.record_status ===
              RECORD_STATUS.ACTIVE
                ? RECORD_STATUS.INACTIVE
                : RECORD_STATUS.ACTIVE,
          },
          selectedRowForToggle?.current?.id as string
        )
      } else {
        await editEntityData(
          entityName,
          entityId,
          layout?.id ?? 0,
          {
            id: selectedRowForToggle?.current?.id,
            record_status:
              selectedRowForToggle?.current?.record_status ===
              RECORD_STATUS.ACTIVE
                ? RECORD_STATUS.INACTIVE
                : RECORD_STATUS.ACTIVE,
          },
          selectedRowForToggle?.current?.id as string
        )
      }
      setTableDataKey((prev) => prev + 1)
    } catch (err) {
      console.error(err)
    } finally {
      setIsLoading(false)
    }
  }

  const isGlobalEntity = () => {
    const entity = entitiesByName[currEntityName ?? '']
    if (!entity || !entity?.source_type) {
      return false
    }
    return entity?.source_type === 'GLOBAL'
  }
  const getActions = (): ListingTableProps['extraActions'] => {
    const hasGlobalPermission = permissionObject.Manage_Global_Schema(
      globalPermissions,
      PERMISSION_MODULES.MANAGE_GLOBAL_DATA
    )
    if (
      isGlobalEntity() &&
      (userData?.profile === USER_PROFILES.STANDARD_USER ||
        userData?.profile === USER_PROFILES.SUPPORT) &&
      !hasGlobalPermission
    )
      return []

    return [
      {
        label: ACTIONS.TOGGLE,
        key: ACTIONS.TOGGLE,
        showInPopUp: false,
        type: 'component',
        renderActionComponent: (row, rowIndex) => {
          return (
            <span>
              <Tooltip
                content={isDraft(row) ? NAMESPACES.CANNOT_ENABLE_DRAFT : ''}
              >
                <ToggleButton
                  data-qa-id={`${ACTIONS.TOGGLE}_${rowIndex}`}
                  name={ACTIONS.TOGGLE}
                  checked={row.record_status === RECORD_STATUS.ACTIVE}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => {
                    if (!isDraft(row)) {
                      selectedRowForToggle.current = row
                      setIsToggleModalOpen(true)
                    }
                  }}
                  disabled={isDraft(row)}
                />
              </Tooltip>
            </span>
          )
        },
      },
    ]
  }

  const noDataScreen = () => (
    <div className="flex flex-col items-center gap-2 justify-center h-96 border border-accent-100 p-2 rounded mt-2">
      <div>
        <SvgEntity />
      </div>
      <div className="font-primary font-light text-xs">
        {hasPermission(MODE_CONSTANTS.ADD, entityId ?? '')
          ? `No data present. Click on "Add ${entityName}" button to add data.`
          : `No data present. You don't have permission to create ${entityName} records.`}
      </div>
    </div>
  )

  const isDraft = (row: DataTypeStore) => {
    return row?.record_status === RECORD_STATUS.DRAFT
  }

  useEffect(() => {
    setTableDataKey((prev) => prev + 1)
  }, [searchQuery])

  useEffect(() => {
    const fetchLayouts = async (): Promise<LayoutResponseType[]> => {
      try {
        setIsLoading(true)
        const response = await getAllLayoutApi({
          entityId: entityId.toString(),
          defaultValue: true,
        })

        return response.data.content ?? []
      } catch (err) {
        console.error(err)
        return []
      } finally {
        setIsLoading(false)
      }
    }

    const updateLayouts = async () => {
      allLayouts.current = await fetchLayouts()

      const selectedLayout = allLayouts.current.find(
        (layout) =>
          layout.type === LAYOUT_TYPE_NAMESPACE.LAYOUT_LISTING &&
          layout.default_value
      )
      if (selectedLayout) {
        setLayout(selectedLayout)
        handleRedirectIdRef.current?.(
          selectedLayout?.parent_entity?.entity_id ??
            selectedLayout?.entity?.entity_id?.toString() ??
            '',
          selectedLayout?.parent_entity?.entity_name ??
            selectedLayout?.entity?.entity_name?.toString() ??
            ''
        )
      }
      setCurrEntityName(
        selectedLayout?.parent_entity?.entity_name ??
          selectedLayout?.entity?.entity_name?.toString() ??
          ''
      )
    }
    if (entityId === 0) return

    const entityExists = entities.some((e) => Number(e.id) === entityId)
    if (entities.length === 0 || !entityExists) {
      setLayout(undefined)
      allLayouts.current = []
      return
    }

    updateLayouts()
  }, [entityId, selectedClientTenantId, entities])

  return (
    <>
      <ConfirmDialog
        message={
          selectedRowForToggle?.current?.record_status === RECORD_STATUS.ACTIVE
            ? NAMESPACES.DISABLE_MSG
            : NAMESPACES.ENABLE_MSG
        }
        onConfirm={async () => {
          setIsToggleModalOpen(false)
          await handleToggle()
          selectedRowForToggle.current = null
        }}
        onCancel={() => {
          selectedRowForToggle.current = null
          setIsToggleModalOpen(false)
        }}
        open={isToggleModalOpen}
      />

      <ListingTable
        dataFetchCallback={fetchUnassignedDataCallback}
        entityId={+entityId}
        entityName={entityName}
        allLayouts={allLayouts.current}
        layout={layout}
        showDefaultActions={['view', 'edit']}
        refetchKey={tableDataKey}
        extraActions={isReadOnlyUser() ? [] : getActions()}
        noDataScreen={noDataScreen}
        isLoading={isLoading}
        persistTableName={`${entityName}-entity`}
        showColumnChooser={true}
        showRestoreButton={true}
        showStringFilterOptions={true}
        setApiFilter={setApiFilter}
        apiFilter={apiFilter}
        disableOnFocusForColumn={true}
      />
    </>
  )
}

export default EntityDataListingTable
